import React, { useEffect } from 'react';
import { Search, Edit, Trash2, Calendar, Clock, MapPin } from 'lucide-react';

const getAvailabilityColor = (isAvailable, isUrgency) => {
  if (isUrgency) return 'bg-red-600';
  return isAvailable ? 'bg-green-600' : 'bg-yellow-600';
};

const SlotList = ({
  slots,
  filterData,
  setFilterData,
  isLoading,
  loadSlots,
  branches,
  equipments,
  handleEditSlot,
  handleDeleteSlot,
}) => {
  const iconProps = { className: 'h-4 w-4' };

  // Recargar slots cuando cambian los filtros
  useEffect(() => {
    loadSlots();
  }, [filterData, loadSlots]);

  const handleFilterChange = (e) => {
    setFilterData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-secondary-main mt-5 rounded shadow">

      {/* Controles de Filtro */}
      <div className="flex flex-wrap justify-between gap-4 p-4 border-b border-gray-700">
        <h2 className="text-lg font-semibold w-full">Filtros de Slots ({slots.length})</h2>

        {/* Filtro por Sucursal */}
        <div className="w-full sm:w-1/3">
          <label className="block text-sm font-medium">Sucursal</label>
          <select name="branchId" value={filterData.branchId} onChange={handleFilterChange} className="w-full rounded border bg-black px-3 py-2 text-white">
            <option value="">Todas las Sucursales</option>
            {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
          </select>
        </div>

        {/* Filtro por Equipo */}
        <div className="w-full sm:w-1/3">
          <label className="block text-sm font-medium">Equipo</label>
          <select name="equipmentId" value={filterData.equipmentId} onChange={handleFilterChange} className="w-full rounded border bg-black px-3 py-2 text-white">
            <option value="">Todos los Equipos</option>
            {equipments.map(e => <option key={e._id} value={e._id}>{e.name} ({e.model})</option>)}
          </select>
        </div>

        <div className="w-full sm:w-1/6 self-end">
          <button onClick={loadSlots} className="w-full rounded bg-blue-600 py-2 text-white hover:bg-blue-800 transition duration-200">
            <Search className="inline-block h-4 w-4 mr-2" /> Buscar
          </button>
        </div>
      </div>

      {/* Lista de Slots */}
      <div className="flex flex-col gap-4 overflow-y-auto p-4">
        {isLoading ? (
          <p className="py-8 text-center text-gray-400">Cargando slots disponibles...</p>
        ) : slots.length > 0 ? (
          slots.map((slot) => (
            <div
              key={slot._id}
              className="bg-secondary-dark border-primary-active gap-4 rounded border p-4 shadow max-sm:flex-col max-sm:gap-3 sm:flex sm:items-center sm:justify-between"
            >
              <div className="flex-1 min-w-[200px]">
                <h3 className="text-md font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {formatDateTime(slot.start)} - {formatDateTime(slot.end).split(', ')[1]}
                </h3>
                <p className="text-sm text-gray-300 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {slot.domain.branch.short_name} ({slot.domain.organization.short_name})
                </p>
              </div>

              <div className="flex flex-col sm:flex-row sm:gap-4 items-start sm:items-center flex-1 min-w-[400px]">

                <span className={`rounded px-2 py-1 text-xs uppercase text-white font-bold ${getAvailabilityColor(slot.is_available, slot.urgency)}`}>
                  {slot.urgency ? '🚨 URGENCIA' : (slot.is_available ? 'DISPONIBLE' : 'RESERVADO')}
                </span>

                <p className="text-xs text-gray-400">Equipo: **{slot.fk_equipment.name}** ({slot.fk_equipment.model})</p>

                <p className="text-xs text-gray-400">Servicio: {slot.fk_service.name}</p>

                <div className="flex gap-2 mt-2 sm:mt-0">
                  <button
                    onClick={() => handleEditSlot(slot)}
                    className="text-blue-300 hover:text-blue-600 p-1"
                    title="Editar slot"
                  >
                    <Edit {...iconProps} />
                  </button>
                  <button
                    onClick={() => handleDeleteSlot(slot._id)}
                    className="text-red-300 hover:text-red-800 p-1"
                    title="Eliminar slot"
                  >
                    <Trash2 {...iconProps} />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="py-8 text-center text-gray-400">No se encontraron bloques de tiempo disponibles.</p>
        )}
      </div>
    </div>
  );
};

export default SlotList;
